/* eslint-disable no-lone-blocks */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Label } from 'reactstrap';
import { updateEpikBox } from '../../../../../actions/EpikBoxes';
import ConfirmationModal from '../../../../ConfirmationModal';
import Toggle from '../../../../Toggle';
import SpinnerCircles from '../../../../Spinner/SpinerCircles';
import EpikboxSip from '../BoxSipPri';
import EpikBoxPri from '../EpikboxPri/EpikboxPri';
import addNote from '../../../../../lib/api/addNote';

export default function EpikBoxSipPir({ sipDetails }) {
  const dispatch = useDispatch();
  const [dcUpdatePopup, setDcUpdatePopup] = useState({ show: false, value: {} });
  const priSettings = useSelector(state => state.AppState.priSipData?.priSettingData);
  const sipSettings = useSelector(state => state.AppState.priSipData?.sipSettingData);

  const token = localStorage.getItem('jwtToken') || null;

  const [warningModal, setWarningModal] = useState(false);
  const [disableWarningModal, setDisableWarningModal] = useState({ show: false, type: null, mode: null });
  const [enableWarningModal, setEnableWarningModal] = useState({ show: false, message: '' });
  const [toggleLoading, setToggleLoading] = useState(null);

  const getDisableWarningText = type =>
    `Warning: Disabling this toggle on an already configured ${type === 'sip' ? 'CAS' : 'PRI'} system will wipe out all configurations, and may prevent functionality. Are you sure you want to proceed?`;
  const getEnableWarningText = type =>
    `Warning: Enabling this toggle on an already configured ${type === 'sip' ? 'CAS' : 'PRI'} system will wipe out all configurations, and may prevent functionality. Are you sure you want to proceed?`;
  const box = useSelector(state => state.AppState.boxDetails.box);
  const [priSipStatus, setPriSipStatus] = useState({
    pri: box?.features?.pri,
    sip: box?.features?.sip,
  });

  const updateBox = async updatedFeature => {
    const response = await dispatch(updateEpikBox(token, { ...box, features: updatedFeature }));
    return response;
  };

  const updatePri = async () => {
    setToggleLoading('pri');
    const updatedFeature = {
      ...box?.features,
      pri: !priSipStatus?.pri,
      sip: priSipStatus?.sip,
    };
    try {
      const updateBoxResponse = await updateBox(updatedFeature);
      
      if (updateBoxResponse) {
        const note = `PRI ${updatedFeature.pri ? 'enabled' : 'disabled'}`
        addNote({ boxID: box._id, note });
      }

      setPriSipStatus({ ...priSipStatus, pri: !priSipStatus?.pri });
    } finally {
      setToggleLoading(null);
    }
  };

  const onPriToggle = async () => {
    if (toggleLoading) return;
    const divElement = document.getElementById('test-time-remaining');
    const isHidden = divElement.getAttribute('hidden') !== null;
    if (isHidden) {
      setDcUpdatePopup({ show: true });
    } else {
      if (priSipStatus?.pri) {
        if (priSettings?.numberSettings?.numbers?.length > 0) {
          toast.error('Please delete all numbers before disabling PRI');
        } else {
          setDisableWarningModal({ show: true, type: 'pri', mode: 'disable' });
        }
      } else if (priSipStatus?.sip) {
        setEnableWarningModal({
          show: true,
          message: 'CAS is currently enabled. Disable CAS first before enabling PRI.',
        });
      } else {
        setDisableWarningModal({ show: true, type: 'pri', mode: 'enable' });
      }
    }
  };
  const unLockBoxButton = async () => {
    const element = document.getElementById('test-lock-icon');
    if (element) {
      // Simulate a click event
      element.click();
    } else {
      console.error('Element with id "test-lock-icon" not found.');
    }
  };

  const updateSip = async () => {
    setToggleLoading('sip');
    const updatedFeature = { ...box?.features, pri: priSipStatus?.pri, sip: !priSipStatus?.sip };
    try {
      const updateBoxResponse = await updateBox(updatedFeature);
      
      if (updateBoxResponse) {
        const note = `CAS ${updatedFeature.sip ? 'enabled' : 'disabled'}`
        addNote({ boxID: box._id, note });
      }

      setPriSipStatus({ ...priSipStatus, sip: !priSipStatus?.sip });
    } finally {
      setToggleLoading(null);
    }
  };

  const handleDisableConfirm = async () => {
    const { type, mode } = disableWarningModal;
    setDisableWarningModal({ show: false, type: null, mode: null });
    if (type === 'pri') {
      await updatePri();
      if (mode === 'enable') {
        setWarningModal(true);
      }
    } else if (type === 'sip') {
      await updateSip();
    }
  };

  const onSipToggle = async () => {
    if (toggleLoading) return;
    const divElement = document.getElementById('test-time-remaining');
    const isHidden = divElement.getAttribute('hidden') !== null;
      if (isHidden) {
        setDcUpdatePopup({ show: true });
      } else {
      if (priSipStatus?.sip) {
        if (sipSettings?.numberSettings?.numbers?.length > 0) {
          toast.error('Please delete all numbers before disabling SIP');
        } else {
          setDisableWarningModal({ show: true, type: 'sip', mode: 'disable' });
        }
      } else if (priSipStatus?.pri) {
        setEnableWarningModal({
          show: true,
          message: 'PRI is currently enabled. Disable PRI first before enabling CAS.',
        });
      } else {
        setDisableWarningModal({ show: true, type: 'sip', mode: 'enable' });
      }
    }
  };

  return (
    <>
      <div style={{ width: '100%', padding: '10px ' }}>
        <div style={{ display: 'flex', verticalAlign: 'center' }}>
          <Label>PRI</Label>
          <div style={{ paddingLeft: '20px', paddingRight: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Toggle value={priSipStatus?.pri} onToggle={onPriToggle} disabled={!!toggleLoading} />
            {toggleLoading === 'pri' && <SpinnerCircles red />}
          </div>
        </div>
        <div style={{ display: 'flex', verticalAlign: 'center' }}>
          <Label>CAS</Label>
          <div style={{ paddingLeft: '20px', paddingRight: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Toggle value={priSipStatus?.sip} onToggle={onSipToggle} disabled={!!toggleLoading} />
            {toggleLoading === 'sip' && <SpinnerCircles red />}
          </div>
        </div>
      </div>
      <div style={{ width: '100%', height: '238px', overflow: 'auto' }}>
        {priSipStatus?.pri && <EpikBoxPri priSipStatus={priSipStatus}  setDcUpdatePopup={setDcUpdatePopup} />}
        {priSipStatus?.sip && <EpikboxSip priSipStatus={priSipStatus} sipDetails={sipDetails}  setDcUpdatePopup={setDcUpdatePopup} />}
      </div>
      <ConfirmationModal
        warning
        bodyText={
          disableWarningModal.mode === 'enable'
            ? getEnableWarningText(disableWarningModal.type)
            : getDisableWarningText(disableWarningModal.type)
        }
        isOpen={disableWarningModal.show}
        onCancel={() => setDisableWarningModal({ show: false, type: null, mode: null })}
        onConfirm={handleDisableConfirm}
      />
      <ConfirmationModal
        warning
        bodyText={enableWarningModal.message}
        isOpen={enableWarningModal.show}
        onCancel={() => setEnableWarningModal({ show: false, message: '' })}
        ignoreConfirmButton
        close
      />
      <ConfirmationModal
        bodyText="Please reboot this device for changes to take effect."
        isOpen={warningModal}
        onCancel={() => setWarningModal(false)}
        ignoreConfirmButton
        close
      />
       <ConfirmationModal
        bodyText={'You are attempting to make changes to a device that is not currently locked for editing. To prevent conflicting changes, please lock the device before proceeding.'}
        headerText={'Device Lock Required'}
        confirmBtnText={'Yes'}
        close={'No'}
        isOpen={dcUpdatePopup.show}
        onCancel={() => {
          setDcUpdatePopup({ show: false, value: {} });
        }}
        onConfirm={() => {
          unLockBoxButton();
          setDcUpdatePopup({ show: false, value: {} });
        }}
      />
    </>
  );
}
